import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Grid, Input, Option, Select, Typography, FormLabel, FormControl } from '@mui/joy';

import DynamicTable, { TableColumn } from '../../components/DynamicTable';
import { TextInputField } from '../../components/FormElements/TextInputField';
import GeneralModal from '../../components/GeneralModal';
import ResponsiveLayout from '../../components/layout/ResponsiveLayout';
import TableHeader from '../../components/TableHeader';
import { DeviceType } from '../../models/DeviceType/DeviceType.model';
import DeviceTypeSchema from '../../schema/deviceTypeSchema';
import { DeviceTypeService } from '../../services/DeviceTypeService';
import sessionStore from '../../stores/SessionStore';

const CATEGORY_OPTIONS = [
  { value: 'DRONE', label: 'Drone' },
  { value: 'SENSOR', label: 'Sensor' },
  { value: 'CAMERA', label: 'Camera' },
  { value: 'CONTROLLER', label: 'Controller' },
  { value: 'STATION', label: 'Ground Station' },
  { value: 'OTHER', label: 'Other' }
];

interface DeviceTypeFormInput {
  name: string;
  category: string;
  manufacturer?: string | null;
  description?: string;
  model_name?: string;
  model_number?: string;
  image?: any | null;
  hardware_id?: string;
}

const DeviceTypeList = () => {
  const { t } = useTranslation();
  const { user } = sessionStore;
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<DeviceType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'add' | 'edit' | 'delete'>('add');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<DeviceTypeFormInput>({
    resolver: yupResolver(DeviceTypeSchema(t)),
    defaultValues: {
      name: '',
      category: '',
      manufacturer: '',
      description: '',
      model_name: '',
      model_number: '',
      image: undefined,
      hardware_id: ''
    }
  });

  const fetchDeviceTypes = async () => {
    setLoading(true);
    try {
      const response = await DeviceTypeService.getDeviceTypes();
      if (response.success) {
        setDeviceTypes(response.data);
      } else {
        throw new Error();
      }
    } catch (error: any) {
      toast.error(t('system_device_types.errors.failed_to_fetch_device_types') || 'Failed to fetch device types');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDeviceTypes();
  }, []);

  const filteredDeviceTypes = !searchQuery
    ? deviceTypes || []
    : (deviceTypes || []).filter(
        (dt) =>
          dt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (dt.category && dt.category.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  const columns: TableColumn<DeviceType, keyof DeviceType>[] = [
    { header: 'Name', key: 'name' },
    { header: 'Category', key: 'category' },
    { header: 'Model Name', key: 'model_name' },
    { header: 'Model Number', key: 'model_number' },
    { header: 'Hardware ID', key: 'hardware_id' }
  ];

  const handleSelectionChange = (selected: DeviceType[]) => {
    setSelectedRows(selected);
  };

  const handleOpenModal = (type: 'add' | 'edit' | 'delete') => {
    setModalType(type);
    if (type === 'edit' && selectedRows.length === 1) {
      const selected = selectedRows[0];
      reset({
        name: selected.name,
        category: selected.category,
        manufacturer: selected.manufacturer,
        description: selected.description,
        model_name: selected.model_name,
        model_number: selected.model_number,
        image: selected.image,
        hardware_id: selected.hardware_id
      });
    } else {
      reset();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const onSubmit = async (data: DeviceTypeFormInput) => {
    const payload: any = { ...data };
    if (!payload.manufacturer) {
      payload.manufacturer = null;
    }
    if (!payload.image) {
      delete payload.image;
    }

    try {
      if (modalType === 'edit' && selectedRows.length === 1) {
        const response = await DeviceTypeService.updateDeviceType(selectedRows[0].id, payload);
        if (response.success) {
          toast.success(t('system_device_types.messages.device_type_updated') || 'Device type updated');
        }
      } else {
        const response = await DeviceTypeService.createDeviceType(data);
        if (response.success) {
          toast.success(t('system_device_types.messages.device_type_created') || 'Device type created');
        }
      }
      fetchDeviceTypes();
      setIsModalOpen(false);
    } catch {
      toast.error(t('system_device_types.errors.failed_to_save') || 'Failed to save device type');
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 1) {
      try {
        const response = await DeviceTypeService.deleteDeviceType(selectedRows[0].id);
        if (response.success) {
          toast.success(t('system_device_types.messages.device_type_deleted') || 'Device type deleted');
          fetchDeviceTypes();
          setIsModalOpen(false);
        }
      } catch {
        toast.error(t('system_device_types.errors.failed_to_delete') || 'Failed to delete device type');
      }
    }
  };

  const modalContent =
    modalType === 'add' || modalType === 'edit' ? (
      <form id='device-type-form' onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid xs={12}>
            <Controller
              control={control}
              name='name'
              render={({ field }) => (
                <TextInputField
                  label='Name'
                  placeholder='Device Type Name'
                  required
                  {...field}
                  error={errors.name?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <FormControl required className='mb-4'>
              <FormLabel>{t('device_types.category')}</FormLabel>
              <Controller
                control={control}
                name='category'
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onChange={(_, value) => field.onChange(value)}
                    placeholder='Select Category'
                    color={errors.category ? 'danger' : 'neutral'}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.category && (
                <Typography color='danger' level='body-sm'>
                  {errors.category.message}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='model_name'
              render={({ field }) => (
                <TextInputField
                  label='Model Name'
                  placeholder='Model Name'
                  {...field}
                  error={errors.model_name?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='model_number'
              render={({ field }) => (
                <TextInputField
                  label='Model Number'
                  placeholder='Model Number'
                  {...field}
                  error={errors.model_number?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='hardware_id'
              render={({ field }) => (
                <TextInputField
                  label='Hardware ID'
                  placeholder='Hardware ID'
                  {...field}
                  error={errors.hardware_id?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='description'
              render={({ field }) => (
                <TextInputField
                  label='Description'
                  placeholder='Description'
                  {...field}
                  error={errors.description?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    ) : (
      <Typography>
        {t('system_device_types.confirm_delete_msg') || 'Are you sure you want to delete this device type?'}
      </Typography>
    );

  const modalActions =
    modalType === 'delete' ? (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('general_button_labels.cancel')}
        </Button>
        <Button color='danger' onClick={handleDelete} variant='solid'>
          {t('general_button_labels.delete')}
        </Button>
      </>
    ) : (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('general_button_labels.cancel')}
        </Button>
        <Button color='neutral' form='device-type-form' type='submit' variant='solid'>
          {t('general_button_labels.save')}
        </Button>
      </>
    );

  return (
    <ResponsiveLayout>
      <TableHeader
        title={
          selectedRows.length > 0
            ? `${selectedRows.length} ${selectedRows.length > 1 ? 'Device Types' : 'Device Type'} Selected`
            : 'Device Types'
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search'
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          {selectedRows.length === 0 &&
            user &&
            user.permissions &&
            user.permissions.some((perm) => ['view_create_device_types'].includes(perm.name)) && (
              <Button color='neutral' onClick={() => handleOpenModal('add')}>
                {t('system_device_types.buttons.create_device_type') || 'Add Device Type'}
              </Button>
            )}
          {selectedRows.length === 1 && (
            <>
              <Button color='neutral' onClick={() => handleOpenModal('edit')}>
                {t('system_device_types.buttons.edit_device_type') || 'Edit'}
              </Button>
              <Button color='danger' onClick={() => handleOpenModal('delete')}>
                {t('general_button_labels.delete')}
              </Button>
            </>
          )}
          {selectedRows.length > 1 && (
            <Button color='danger' onClick={() => handleOpenModal('delete')}>
              {t('general_button_labels.delete')}
            </Button>
          )}
        </Box>
      </TableHeader>

      <DynamicTable
        columns={columns}
        data={filteredDeviceTypes}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        checkbox
      />

      <GeneralModal
        actions={modalActions}
        content={modalContent}
        onClose={handleCloseModal}
        open={isModalOpen}
        title={
          modalType === 'add'
            ? t('system_device_types.buttons.create_device_type') || 'Add Device Type'
            : modalType === 'edit'
            ? t('system_device_types.buttons.edit_device_type') || 'Edit Device Type'
            : t('general_button_labels.delete') || 'Delete'
        }
      />
    </ResponsiveLayout>
  );
};

export default DeviceTypeList;
